
import React, { memo, useState, useEffect, useMemo, useRef } from 'react';
import {
  DocumentTextIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  QrCodeIcon,
  UserIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

import Table from '../common/Table';
import StatusBadge from '../common/StatusBadge';

// Utility to convert strings to Pascal format (Title Case)
const pascalCase = (str) =>
  typeof str === 'string' && str.length > 0
    ? str.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    : '';

// Helper: get document type label from doc
const getDocumentTypeLabel = (doc) => {
  if (!doc) return '';
  const dt = doc.document_type;
  if (typeof dt === 'object' && dt && dt.label) return dt.label;
  if (typeof dt === 'string') return dt.replace(/_/g, ' ').replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  return '';
};

// Helper: get initials from resident name
const getInitials = (name = '') => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
};

const DocumentsTable = ({
  documents = [],
  isLoading = false,
  onView,
  onEdit,
  onDelete,
  onTrack,
  onChangeStatus,
  searchValue,
  onSearchChange,
  onSearch,
  onFilter,
  pagination,
  onPageChange
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [typeFilter, setTypeFilter] = useState('All Types');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');

  const isInitialMount = useRef(true);
  // Notify parent when filters change (for server-side filtering); skip initial mount to avoid extra refetch
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (typeof onFilter === 'function') {
      onFilter({
        document_type: typeFilter === 'All Types' ? '' : typeFilter,
        status: statusFilter === 'All Status' ? '' : statusFilter,
        date_from: dateFromFilter || '',
        date_to: dateToFilter || '',
      });
    }
  }, [typeFilter, statusFilter, dateFromFilter, dateToFilter, onFilter]);

  // Unique document types and statuses for dropdown options.
  // We keep a union of all values we've seen so options don't disappear
  // when the current result set is filtered.
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  useEffect(() => {
    setDocumentTypeOptions((prev) => {
      const set = new Set(prev);
      documents.forEach((doc) => {
        const label = getDocumentTypeLabel(doc);
        if (label) set.add(label);
      });
      return Array.from(set).sort();
    });

    setStatusOptions((prev) => {
      const set = new Set(prev);
      documents.forEach((doc) => {
        const s = doc.status ? String(doc.status).trim() : '';
        if (s) set.add(s);
      });
      return Array.from(set).sort();
    });
  }, [documents]);

  // Client-side filtered list (so filters always apply even if API ignores params)
  const filteredDocuments = useMemo(() => {
    return documents.filter((doc) => {
      if (statusFilter !== 'All Status') {
        const docStatus = (doc.status || '').toString().trim();
        const filterStatus = statusFilter.toString().trim();
        if (docStatus.toLowerCase() !== filterStatus.toLowerCase()) return false;
      }
      if (typeFilter !== 'All Types') {
        const label = getDocumentTypeLabel(doc);
        if (label !== typeFilter) return false;
      }
      const reqDate = doc.request_date;
      if (dateFromFilter && reqDate) {
        const d = new Date(reqDate);
        const from = new Date(dateFromFilter);
        if (d < from) return false;
      }
      if (dateToFilter && reqDate) {
        const d = new Date(reqDate);
        const to = new Date(dateToFilter);
        to.setHours(23, 59, 59, 999);
        if (d > to) return false;
      }
      if ((dateFromFilter || dateToFilter) && !reqDate) return false;
      return true;
    });
  }, [documents, typeFilter, statusFilter, dateFromFilter, dateToFilter]);

  const columns = [
    {
      key: 'tracking_number',
      label: 'DOCUMENT & TRACKING #',
      render: (_, doc) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm">
            <DocumentTextIcon className="w-5 h-5" />
          </div>
          <div>
            <div className=" text-gray-500 text-xs">
              {doc.document_type && typeof doc.document_type === 'object'
                ? doc.document_type.label
                : typeof doc.document_type === 'string'
                  ? doc.document_type
                    .replace(/_/g, ' ')
                    .replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                  : 'Unknown'}
            </div>
            <div className="text-sm font-medium">{doc.tracking_number}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'resident',
      label: 'RESIDENT',
      render: (_, doc) => (
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 h-8 w-8 relative">
            {doc.resident && doc.resident.photo_url ? (
              <img
                src={doc.resident.photo_url}
                alt={`${doc.resident.firstname} ${doc.resident.lastname}`}
                className="h-8 w-8 rounded-full object-cover border-2 border-blue-200 hover:scale-110 transition-transform duration-200"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const fallback = e.target.parentNode.querySelector('.fallback-avatar');
                  fallback.style.display = 'flex';
                }}
              />
            ) : null}
            <div className={`fallback-avatar h-8 w-8 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-sm hover:scale-110 transition-transform duration-200 ${doc.resident && doc.resident.photo_url ? 'hidden' : 'flex'}`}>
              {doc.resident
                ? `${doc.resident.first_name?.charAt(0) || ''}${doc.resident.last_name?.charAt(0) || ''}`.toUpperCase()
                : getInitials(doc.resident_name || doc.first_name || doc.last_name || 'N R')}
            </div>
          </div>
          <div>
            <div className="font-medium text-gray-900 text-xs">
              {doc.resident && (doc.resident.first_name || doc.resident.last_name)
                ? `${pascalCase(doc.resident.first_name || '')} ${pascalCase(doc.resident.last_name || '')}`.trim()
                : doc.resident_name
                  ? `${doc.resident_name} `
                  : `${pascalCase(doc.first_name || '')} ${pascalCase(doc.last_name || '')}`.trim() || 'Non-Resident'}
            </div>
            <div className="text-xs text-gray-400">
              {doc.resident && doc.resident.barangay_id_number
                ? doc.resident.barangay_id_number
                : <span className="text-red-400">(Non-Resident)</span>}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'STATUS',
      render: (_, doc) => <StatusBadge status={doc.status} />,
    },
    {
      key: 'fee_amount',
      label: 'FEE AMOUNT',
      render: (_, doc) => {
        let amount = doc.fee_amount;
        if (typeof amount === 'string') {
          amount = parseFloat(amount);
        }
        return (
          <span className="text-xs text-gray-900">
            {typeof amount === 'number' && !isNaN(amount)
              ? `₱ ${amount.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : '—'}
          </span>
        );
      },
    },
    {
      key: 'request_date',
      label: 'REQUESTED',
      render: (_, doc) => (
        <span className="text-xs text-gray-500">
          {doc.request_date
            ? new Date(doc.request_date).toLocaleString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })
            : 'N/A'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'ACTIONS',
      render: (_, doc) => (
        <div className="flex justify-end space-x-2">
          <button
            className="text-blue-600 hover:text-blue-900 p-1.5 rounded-full hover:bg-blue-50 transition-all duration-200 hover:scale-110"
            title="View"
            onClick={() => onView(doc)}
          >
            <EyeIcon className="w-4 h-4" />
          </button>
          <button
            className="text-yellow-600 hover:text-yellow-900 p-1.5 rounded-full hover:bg-yellow-50 transition-all duration-200 hover:scale-110"
            title="Edit"
            onClick={() => onEdit(doc)}
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button
            className="text-purple-600 hover:text-purple-900 p-1.5 rounded-full hover:bg-purple-50 transition-all duration-200 hover:scale-110"
            title="Change Status"
            onClick={() => onChangeStatus && onChangeStatus(doc)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6l4 2" /><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" fill="none" /></svg>
          </button>
          <button
            className="text-red-600 hover:text-red-900 p-1.5 rounded-full hover:bg-red-50 transition-all duration-200 hover:scale-110"
            title="Delete"
            onClick={() => onDelete(doc)}
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Table
        header={
          <div className="mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h3 className="text-lg font-medium text-gray-900">Documents</h3>

              <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                <input
                  value={searchValue}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder="Search..."
                  className="border border-gray-300 rounded-lg px-4 py-2 text-sm w-64 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                >
                  <FunnelIcon className="h-4 w-4 mr-1.5" />
                  Filters
                </button>
              </div>
            </div>

            {showFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                {/* Document Type */}
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Document Type</label>
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option>All Types</option>
                    {documentTypeOptions.map((opt) => (
                      <option key={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option>All Status</option>
                    {statusOptions.map((opt) => (
                      <option key={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                {/* Date From */}
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Date From</label>
                  <input
                    type="date"
                    value={dateFromFilter}
                    onChange={(e) => setDateFromFilter(e.target.value)}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                {/* Date To */}
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Date To</label>
                  <input
                    type="date"
                    value={dateToFilter}
                    onChange={(e) => setDateToFilter(e.target.value)}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}
          </div>
        }
        columns={columns}
        data={filteredDocuments}
        getRowKey={(doc) => doc.id}
        pagination={pagination}
        onPageChange={onPageChange}
        paginationProps={{
          isLoading,
          showSizeSelector: true,
        }}
      />
    </div>
  );
};

export default memo(DocumentsTable);
